import {Component, DestroyRef, HostListener} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowDown,
    faArrowUp,
    faFileExcel,
    faPen,
    faSquarePlus,
    faSquareXmark,
    faTrash,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import {ApiService} from '../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {getFileNameFromResponse} from '../../common/utils';
import saveAs from 'file-saver';
import {GenericSearchComponent} from '../shared/search-suggestion/search-suggestion.component';
import {VehicleNumberCopyDirective} from '../shared/vehicle-number-copy/vehicle-number-copy.directive';
import {BulkImportResultComponent} from '../shared/bulk-import-result/bulk-import-result.component';
import {BulkUploadFlow} from '../shared/bulk-upload-flow/bulk-upload-flow';
import {VEHICLE_MONITORING_BULK_UPLOAD_SHELL} from '../shared/bulk-upload-flow/bulk-upload-shell.configs';
import {BulkUploadShellComponent} from '../shared/bulk-upload-shell/bulk-upload-shell.component';
import {
    BulkDeleteImpactItem,
    BulkDeletePreviewShellComponent,
} from '../shared/bulk-delete-preview-shell/bulk-delete-preview-shell.component';
import {
    buildDeletePayload,
    BulkSelectionState,
    clearSelection,
    createEmptySelection,
    hasSelection,
    isKeySelected,
    isPageFullySelected,
    selectAllMatching,
    selectedCount,
    selectVisiblePage,
    SharedBulkSelectionBannerComponent,
    toggleKey,
} from '../shared/bulk-selection';
import {MonitoringTypeBadgeComponent} from '../shared/monitoring-type-badge/monitoring-type-badge.component';
import {SeverityBadgeComponent} from '../shared/severity-badge/severity-badge.component';
import {
    MonitoringTypeMultiselectComponent
} from '../shared/monitoring-type-multiselect/monitoring-type-multiselect.component';
import {VEHICLE_MONITORING_SEVERITY_OPTIONS} from '../shared/vehicle-monitoring/vehicle-monitoring.constants';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';
import {encodeSortColumnParam, toggleColumnSort} from '../shared/utils/table-sort.utils';
import {ReportingDatePipe} from '../shared/datetime/reporting-date.pipe';
import {RoleService} from '../services/role.service';
import {AuthService} from '../services/auth.service';
import {VtePaginationComponent} from '../shared/vte/vte-pagination/vte-pagination.component';
import {SharedTableToolbarComponent} from '../shared/table-toolbar/shared-table-toolbar.component';

export type GroupedMonitoringRule = {
    id: number;
    type: string;
    type_display: string;
    severity: string;
    severity_display: string;
    remarks: string;
};

export type GroupedVehicleMonitoring = {
    id: number;
    vehicle_number: string;
    monitoring_rules: GroupedMonitoringRule[];
    highest_severity: string;
    highest_severity_display: string;
    remarks: string;
    created_by: string;
    created_on: string;
};

@Component({
    selector: 'app-vehicle-monitoring',
    imports: [ReportingDatePipe, 
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        GenericSearchComponent,
        VehicleNumberCopyDirective,
        BulkImportResultComponent,
        BulkUploadShellComponent,
        BulkDeletePreviewShellComponent,
        SharedBulkSelectionBannerComponent,
        MonitoringTypeBadgeComponent,
        SeverityBadgeComponent,
        MonitoringTypeMultiselectComponent,
        VtePaginationComponent,
        SharedTableToolbarComponent,
    ],
    templateUrl: './vehicle-monitoring.component.html',
    styleUrl: './vehicle-monitoring.component.css'
})
export class VehicleMonitoringComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
    searchText: string | null = '';
    selectedAlertTypes: string[] = [];
    selectedSeverities: string[] = [];
    severityFilterOptions = VEHICLE_MONITORING_SEVERITY_OPTIONS;
    severityFilterOrder = VEHICLE_MONITORING_SEVERITY_OPTIONS.map(option => option.value);
    recordsPerPage: number = 10;
    currentPage: number = 1;
    vehicles: GroupedVehicleMonitoring[] = [];
    nextPage: number | null = null;
    previousPage: number | null = null;
    showDeleteDialog = false;
    selectedVehicle: GroupedVehicleMonitoring | null = null;
    selectedRuleIds = new Set<number>();
    deleting = false;
    totalRecords = 0;
    totalMonitoringRules = 0;
    role = '';
    todayDate: Date = new Date();
    hoverRowKey: string | null = null;
    sortColumn = '';
    sortDirection: 'asc' | 'desc' | '' = '';
    hoveredColumn = '';
    bulkSelection: BulkSelectionState<string> = createEmptySelection();
    showBulkDeleteDialog = false;
    bulkDeletePreview: any = null;
    bulkDeleting = false;
    bulkDeleteWillDelete: BulkDeleteImpactItem[] = [];
    bulkDeleteRetained: BulkDeleteImpactItem[] = [];
    readonly bulkDeleteAssurances = [
        'Historical Vehicle Logs are NOT deleted.',
        'Historical Alert Logs are NOT deleted.',
    ];

    readonly bulkUploadShell = VEHICLE_MONITORING_BULK_UPLOAD_SHELL;
    readonly bulkUpload: BulkUploadFlow;

    constructor(
        private apiService: ApiService,
        library: FaIconLibrary,
        private router: Router,
        private toastr: ToastrService,
        private roleService: RoleService,
        private authService: AuthService,
        private destroyRef: DestroyRef,
    ) {
        this.bulkUpload = new BulkUploadFlow(this.toastr, {
            failureFilename: 'vehicle_alert_rules_bulk_upload_failures.csv',
            defaultSuccessMessage: 'Monitoring records uploaded successfully.',
            upload: (formData) => this.apiService.bulkUploadVehicleMonitoring(formData),
        }, this.destroyRef);
        this.fetchData(this.currentPage, this.recordsPerPage);
        library.addIcons(faFileExcel, faAngleRight, faAngleLeft, faUpload, faSquarePlus, faSquareXmark, faPen, faTrash, faArrowUp, faArrowDown);

        const user = localStorage.getItem('user');
        if (user) {
            const userObj = JSON.parse(user);
            this.role = userObj['groups'];
        }
    }

    get canBulkDelete(): boolean {
        const userInfo = this.authService.getCurrentUserInfo?.() ?? null;
        const groups = Array.isArray(userInfo?.role) ? userInfo.role : [userInfo?.role].filter(Boolean);
        return this.roleService.isPlatformSuperuser() || this.roleService.isAdmin(groups);
    }

    get visiblePageVehicleNumbers(): string[] {
        return (this.vehicles || [])
            .map((row) => row?.vehicle_number)
            .filter((plate): plate is string => !!plate);
    }

    get bulkSelectedCount(): number {
        return selectedCount(this.bulkSelection);
    }

    get hasBulkSelection(): boolean {
        return hasSelection(this.bulkSelection);
    }

    get allVisibleVehiclesSelected(): boolean {
        return isPageFullySelected(this.bulkSelection, this.visiblePageVehicleNumbers);
    }

    isVehicleSelected(vehicleNumber: string): boolean {
        return isKeySelected(this.bulkSelection, vehicleNumber);
    }

    toggleVehicleSelection(vehicleNumber: string, checked: boolean): void {
        this.bulkSelection = toggleKey(this.bulkSelection, vehicleNumber, checked);
    }

    toggleSelectAllVehicles(checked: boolean): void {
        this.bulkSelection = selectVisiblePage(
            this.bulkSelection,
            this.visiblePageVehicleNumbers,
            checked,
        );
    }

    onSelectAllMatching(): void {
        this.bulkSelection = selectAllMatching(this.bulkSelection, this.totalRecords);
    }

    onClearBulkSelection(): void {
        this.bulkSelection = clearSelection();
    }

    private buildBulkDeletePayload(): Record<string, unknown> {
        return buildDeletePayload(this.bulkSelection, {
            idField: 'vehicle_numbers',
            filters: {
                search: this.searchText || '',
                alert_type: this.selectedAlertTypes,
                severity: this.selectedSeverities,
            },
        });
    }

    openBulkDeletePreview(): void {
        if (!this.canBulkDelete || !this.hasBulkSelection) {
            return;
        }
        this.apiService.previewMonitoringBulkDelete(this.buildBulkDeletePayload()).subscribe({
            next: (preview: any) => {
                this.bulkDeletePreview = preview;
                this.bulkDeleteWillDelete = [
                    {label: 'Selected monitored vehicles', count: preview?.selected_records || 0},
                    {label: 'Vehicle groups', count: preview?.will_delete?.vehicle_groups || 0},
                    {label: 'Monitoring rules', count: preview?.will_delete?.monitoring_rules || 0},
                ];
                this.bulkDeleteRetained = [
                    {label: 'Vehicle database', count: preview?.related_retained?.vehicles || 0},
                    {label: 'Hotlist entries', count: preview?.related_retained?.hotlisted || 0},
                    {label: 'Blacklist entries', count: preview?.related_retained?.blacklisted || 0},
                    {label: 'Vehicle logs (History)', count: preview?.related_retained?.vehicle_logs || 0},
                    {label: 'Alert logs (History)', count: preview?.related_retained?.alert_logs || 0},
                ];
                this.showBulkDeleteDialog = true;
            },
            error: (err) => {
                this.toastr.error(err?.error?.detail || 'Unable to analyze delete impact.', 'Error');
            },
        });
    }

    closeBulkDeleteDialog(): void {
        if (this.bulkDeleting) {
            return;
        }
        this.showBulkDeleteDialog = false;
        this.bulkDeletePreview = null;
    }

    confirmBulkDelete(): void {
        if (!this.canBulkDelete || !this.hasBulkSelection || this.bulkDeleting) {
            return;
        }
        this.bulkDeleting = true;
        this.apiService.bulkDeleteMonitoring(this.buildBulkDeletePayload()).subscribe({
            next: (result: any) => {
                this.bulkDeleting = false;
                this.showBulkDeleteDialog = false;
                this.bulkDeletePreview = null;
                this.bulkSelection = clearSelection();
                const deleted = result?.deleted_vehicle_groups ?? 0;
                this.toastr.success(
                    `${deleted} monitored vehicle group${deleted === 1 ? '' : 's'} removed.`,
                    'Success',
                );
                this.fetchData(this.currentPage, this.recordsPerPage);
            },
            error: (err) => {
                this.bulkDeleting = false;
                this.toastr.error(err?.error?.detail || 'Bulk delete failed.', 'Error');
            },
        });
    }

    get searchExtraBody(): Record<string, string> {
        const body: Record<string, string> = {};
        if (this.selectedAlertTypes.length) {
            body['alert_types'] = this.selectedAlertTypes.join(',');
        }
        if (this.selectedSeverities.length) {
            body['severities'] = this.selectedSeverities.join(',');
        }
        return body;
    }

    fetchData(page: number, pageSize: number) {
        const ordering = this.sortColumn
            ? encodeSortColumnParam({
                sortColumn: this.sortColumn,
                sortDirection: this.sortDirection,
            })
            : null;
        this.apiService.getGroupedVehicleMonitoring(
            page,
            pageSize,
            this.searchText,
            this.selectedAlertTypes,
            this.selectedSeverities,
            ordering,
        ).subscribe({
            next: (data: any) => {
                this.vehicles = data['results'];
                this.totalRecords = data['count'];
                this.totalMonitoringRules = data['total_monitoring_rules'] ?? 0;
                this.nextPage = data['next'];
                this.previousPage = data['previous'];
            }
        });
    }

    sortData(column: string) {
        const next = toggleColumnSort(
            {sortColumn: this.sortColumn, sortDirection: this.sortDirection},
            column,
        );
        this.sortColumn = next.sortColumn;
        this.sortDirection = next.sortDirection;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    get totalPages(): number {
        let totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
        if (totalPage === 0) {
            totalPage = 1;
        }
        return totalPage;
    }

    next() {
        if (this.nextPage) {
            this.currentPage++;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    previous() {
        if (this.previousPage) {
            this.currentPage--;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    selectPageSize() {
        if (this.recordsPerPage) {
            this.currentPage = 1;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    applyAlertTypeFilter(selectedValues: string[]) {
        this.selectedAlertTypes = selectedValues;
        this.currentPage = 1;
        this.bulkSelection = clearSelection();
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    applySeverityFilter(selectedValues: string[]) {
        this.selectedSeverities = selectedValues;
        this.currentPage = 1;
        this.bulkSelection = clearSelection();
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    openDeleteDialog(vehicle: GroupedVehicleMonitoring) {
        this.selectedVehicle = vehicle;
        this.selectedRuleIds = new Set(vehicle.monitoring_rules.map(rule => rule.id));
        this.showDeleteDialog = true;
    }

    closeDeleteDialog() {
        if (this.deleting) {
            return;
        }
        this.showDeleteDialog = false;
        this.selectedVehicle = null;
        this.selectedRuleIds.clear();
    }

    isRuleSelected(ruleId: number): boolean {
        return this.selectedRuleIds.has(ruleId);
    }

    toggleRuleSelection(ruleId: number, checked: boolean) {
        if (checked) {
            this.selectedRuleIds.add(ruleId);
            return;
        }
        this.selectedRuleIds.delete(ruleId);
    }

    editVehicle(id: number) {
        this.router.navigate(['/edit-vehicle-monitoring', id]);
    }

    deleteSelectedRules() {
        if (!this.selectedVehicle || this.selectedRuleIds.size === 0 || this.deleting) {
            return;
        }

        this.deleting = true;
        this.apiService.deleteVehicleMonitoringRules(Array.from(this.selectedRuleIds)).subscribe({
            next: () => {
                this.deleting = false;
                this.toastr.success('Selected monitoring types removed successfully.', 'Success');
                this.closeDeleteDialog();
                this.fetchData(this.currentPage, this.recordsPerPage);
            },
            error: (err: any) => {
                this.deleting = false;
                this.handleError(err);
            },
        });
    }

    exportToExcel() {
        this.apiService.exportVehicleMonitoringRules(
            this.searchText,
            this.selectedAlertTypes,
            this.selectedSeverities,
        ).subscribe({
            next: (response: any) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err: any) => this.toastr.error(`${err}`, 'Error'),
        });
    }

    addVehicle() {
        this.router.navigate(['/add-vehicle-monitoring']);
    }

    downloadTemplate() {
        this.apiService.downloadImportTemplate('vehicle_alert_rules').subscribe({
            next: (response: any) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err: any) => this.handleError(err),
        });
    }

    openBulkUploadDialog() {
        this.bulkUpload.open();
    }

    closeBulkUploadDialog() {
        this.bulkUpload.close();
    }

    onBulkFileSelected(file: File) {
        this.bulkUpload.selectFile(file);
    }

    submitBulkUpload(file?: File) {
        this.bulkUpload.submit(file, () => {
            this.fetchData(this.currentPage, this.recordsPerPage);
        });
    }

    onResults(event: {
        searchText: string;
        results: any[];
        totalRecords: number;
        totalMonitoringRules?: number;
        nextPage: number | null;
        previousPage: number | null;
    }) {
        this.currentPage = 1;
        this.recordsPerPage = 10;
        this.previousPage = event.previousPage;
        this.nextPage = event.nextPage;
        this.searchText = event.searchText;
        this.vehicles = event.results;
        this.totalRecords = event.totalRecords;
        this.totalMonitoringRules = event.totalMonitoringRules ?? 0;
        this.bulkSelection = clearSelection();
    }

    private handleError(err: any) {
        if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
        } else if (err?.error?.alert_type) {
            err.error.alert_type.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.status !== 403 && err.status !== 0) {
            this.toastr.error('Something went wrong!', 'Error');
        }
        this.closeDeleteDialog();
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        const target = event.target as HTMLElement;
        const modalElement = document.querySelector('.delete-monitoring-modal .modal-content');
        if (modalElement && !modalElement.contains(target) && this.showDeleteDialog && !this.deleting) {
            this.closeDeleteDialog();
        }
    }
}
